import { Button, Card, MonoLabel, cn, cssVar } from '@/design-system';
import {
    lifecycleNumber,
    type LifecyclePhase,
    type LifecycleStep,
    type OrderStatusCode,
} from '@/entities/order';
import { formatDayShort } from '@/shared/lib';

/**
 * De levenscyclus van een keuring als zeven kaartjes. Staat op Keuringen
 * (scherm 5) en in het Dossier (scherm 8).
 *
 * De fase komt uit de status: gezet is wit met een accentstip, de huidige stap
 * heeft een accentrand op lichte tint met een amberstip, en wat nog komt staat
 * gestippeld. Onder de naam staat het moment van die stap als de keuring het
 * kent - geen voorbeeldtekst.
 *
 * Met `onStep` krijgt elke stap die volgens `allowed_transitions` nu kan een
 * knop. De app kiest die stappen nooit zelf.
 */
const dotColor: Record<LifecyclePhase, string> = {
    done: cssVar.accent,
    now: cssVar.warn,
    todo: cssVar.neutralDot,
};

export interface LifecycleStripProps {
    steps: readonly LifecycleStep[];
    /** `md` in het dossier (titel 13px), `sm` in de detailbalk (12px). */
    size?: 'sm' | 'md';
    label?: string;
    /** Maakt de toegestane stappen klikbaar. */
    onStep?: (status: OrderStatusCode) => void;
    stepLabel?: (step: LifecycleStep) => string;
    busy?: boolean;
}

export function LifecycleStrip({
    steps,
    size = 'sm',
    label = 'Levenscyclus van deze keuring',
    onStep,
    stepLabel = (step) => `Zet op ${step.label}`,
    busy = false,
}: LifecycleStripProps) {
    return (
        <ol aria-label={label} className="m-0 flex list-none flex-wrap gap-2 p-0">
            {steps.map((step, index) => (
                <Card
                    as="li"
                    key={step.status}
                    radius={9}
                    variant={
                        step.phase === 'now'
                            ? 'accent'
                            : step.phase === 'todo'
                              ? 'muted'
                              : 'default'
                    }
                    aria-current={step.phase === 'now' ? 'step' : undefined}
                    data-phase={step.phase}
                    className={cn(
                        'flex min-w-[140px] flex-[1_1_148px] flex-col px-[13px] py-3',
                        step.phase === 'now' && 'border-[var(--accent)]',
                    )}
                >
                    <div className="mb-[6px] flex items-center gap-[7px]">
                        <span
                            aria-hidden="true"
                            className="h-[7px] w-[7px] shrink-0 rounded-full"
                            style={{ background: dotColor[step.phase] }}
                        />
                        <MonoLabel size={size === 'md' ? 9 : 10} tone="muted">
                            {lifecycleNumber(index)}
                        </MonoLabel>
                    </div>
                    <div
                        className={cn(
                            'font-medium text-[var(--text-primary)]',
                            size === 'md'
                                ? 'text-[13px] leading-[1.3]'
                                : 'text-[12px] leading-[1.3]',
                        )}
                    >
                        {step.label}
                    </div>
                    {step.at ? (
                        <MonoLabel
                            size={10}
                            variant="value"
                            tone="muted"
                            as="div"
                            className="mt-[3px]"
                        >
                            {formatDayShort(step.at)}
                        </MonoLabel>
                    ) : null}
                    {onStep && step.allowed ? (
                        <Button
                            variant="accentOutline"
                            size="xs"
                            className="mt-2 self-start"
                            disabled={busy}
                            onClick={() => onStep(step.status)}
                        >
                            {stepLabel(step)}
                        </Button>
                    ) : null}
                </Card>
            ))}
        </ol>
    );
}
